import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import CustomSelect from '../../../../common/formfields/CustomSelect';
import { primaryColor, secondaryColor } from '../../../../../utils/comon';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';

const UnitForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);

    const [sites, setSites] = useState([]);
    const [allWings, setAllWings] = useState([]);
    const [allFloors, setAllFloors] = useState([]);
    
    const [wings, setWings] = useState([]);
    const [floors, setFloors] = useState([]);

    const [formData, setFormData] = useState({
        unitId: null,
        siteId: null,
        wingId: null,
        floorId: null,
        unitName: '',
        sqft: '',
        isActive: 'Active'
    });

    const isEdit = !!location.state?.id;
    const editItem = location.state?.item;

    useEffect(() => {
        const fetchMasterData = async () => {
            try {
                // Fetch Sites
                const siteRes = await apiServices({ method: 'GET', url: '/ResidentialSite/GetAllResidentialSites' });
                if (siteRes.success || siteRes.isSuccess || siteRes.IsSuccess || siteRes) {
                    let siteData = Array.isArray(siteRes.data) ? siteRes.data : (siteRes.data?.data || siteRes);
                    if (Array.isArray(siteData)) {
                        setSites(siteData.filter(s => s.IsActive === 'Active' || s.isActive === 'Active').map(s => ({
                            label: s.SiteName || s.siteName,
                            value: s.SiteId || s.siteId
                        })));
                    }
                }

                // Fetch Wings
                const wingRes = await apiServices({ method: 'GET', url: '/ResWingMaster/GetAllWingMaster' });
                if (wingRes.success || wingRes.isSuccess || wingRes.IsSuccess || wingRes) {
                    let wingData = Array.isArray(wingRes.data) ? wingRes.data : (wingRes.data?.data || wingRes);
                    if (Array.isArray(wingData)) setAllWings(wingData);
                }

                // Fetch Floors
                const floorRes = await apiServices({ method: 'GET', url: '/ResFloorMaster/getAllFloors' });
                if (floorRes.success || floorRes.isSuccess || floorRes.IsSuccess || floorRes) {
                    let floorData = Array.isArray(floorRes.data) ? floorRes.data : (floorRes.data?.data || floorRes);
                    if (Array.isArray(floorData)) setAllFloors(floorData);
                }

                if (isEdit && editItem) {
                    setFormData({
                        unitId: editItem.unitId || editItem.UnitId || null,
                        siteId: editItem.siteId || editItem.SiteId || null,
                        wingId: editItem.wingId || editItem.WingId || null,
                        floorId: editItem.floorId || editItem.FloorId || null,
                        unitName: editItem.unitName || editItem.UnitName || editItem.unitNumber || editItem.UnitNumber || '',
                        sqft: editItem.sqft || editItem.Sqft || '',
                        isActive: editItem.isActive || editItem.IsActive || (editItem.isActiveId === 1 ? 'Active' : 'Inactive') || 'Active'
                    });
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                toast.error("Failed to load necessary data.");
            }
        };

        fetchMasterData();
    }, [isEdit, editItem]);

    // Filter Wings based on Site
    useEffect(() => {
        if (!formData.siteId) {
            setWings([]);
            return;
        }
        const filteredWings = allWings
            .filter(w => (w.siteId || w.SiteId) === formData.siteId && (w.IsActive === 'Active' || w.isActive === 'Active' || w.isActiveId === 1))
            .map(w => ({
                label: w.WingName || w.wingName,
                value: w.WingId || w.wingId || w.Id || w.id
            }));
        setWings(filteredWings);
    }, [formData.siteId, allWings]);

    // Filter Floors based on Site AND Wing
    useEffect(() => {
        if (!formData.siteId || !formData.wingId) {
            setFloors([]);
            return;
        }
        const filteredFloors = allFloors
            .filter(f => (f.siteId || f.SiteId) === formData.siteId && (f.wingId || f.WingId) === formData.wingId && (f.IsActive === 'Active' || f.isActive === 'Active' || f.isActiveId === 1))
            .map(f => ({
                label: f.FloorNumber || f.floorNumber || f.FloorName || f.floorName,
                value: f.FloorId || f.floorId || f.Id || f.id
            }));
        setFloors(filteredFloors);
    }, [formData.siteId, formData.wingId, allFloors]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const handleSelectChange = (key, option) => {
        setFormData(prev => {
            const newData = { ...prev, [key]: option ? option.value : null };
            if (key === 'siteId') {
                newData.wingId = null;
                newData.floorId = null;
            } else if (key === 'wingId') {
                newData.floorId = null;
            }
            return newData;
        });
    };

    const validateForm = () => {
        if (!formData.siteId) return toast.warning("Site is required"), false;
        if (!formData.wingId) return toast.warning("Wing is required"), false;
        if (!formData.floorId) return toast.warning("Floor is required"), false;
        if (!formData.unitName || !formData.unitName.trim()) return toast.warning("Unit Name/Number is required"), false;
        if (!formData.isActive) return toast.warning("Status is required"), false;
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setIsLoading(true);

        const payload = {
            unitId: formData.unitId || 0,
            siteId: formData.siteId,
            wingId: formData.wingId,
            floorId: formData.floorId,
            unitName: formData.unitName,
            unitNumber: formData.unitName,
            sqft: formData.sqft || "0",
            createdBy: 'sys',
            isActiveId: formData.isActive === 'Active' ? 1 : 0
        };

        try {
            const url = isEdit ? `/ResidentialUnitMaster/UpdateUnitById` : `/ResidentialUnitMaster/AddNewUnit`;
            const method = isEdit ? 'PUT' : 'POST';

            const response = await apiServices({ method, url, data: payload });

            if (response.success || response.isSuccess || response.IsSuccess || response) {
                toast.success(`Residential Unit ${isEdit ? 'updated' : 'created'} successfully!`);
                navigate('/zentres/master-management/unit');
            } else {
                toast.error(response.message || `Failed to ${isEdit ? 'update' : 'create'} unit.`);
            }
        } catch (error) {
            console.error("Submission error:", error);
            toast.error("An error occurred during submission.");
        } finally {
            setIsLoading(false);
        }
    };

    const selectedSite = sites.find(s => s.value === formData.siteId) || null;
    const selectedWing = wings.find(w => w.value === formData.wingId) || null;
    const selectedFloor = floors.find(f => f.value === formData.floorId) || null;

    return (
        <div className="bg-white px-6 py-4 border flex flex-col rounded-lg flex-1 overflow-y-auto pt-4">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading={`${isEdit ? 'Update' : 'Add'} Residential Unit`}
                subHeading={`Please fill out the form below to ${isEdit ? 'update the existing' : 'create a new'} residential unit.`}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
                <CustomSelect
                    label={<>Site Name <span className="text-red-500">*</span></>}
                    options={sites}
                    value={selectedSite}
                    onChange={(opt) => handleSelectChange('siteId', opt)}
                />

                <CustomSelect
                    label={<>Wing Name <span className="text-red-500">*</span></>}
                    options={wings}
                    value={selectedWing}
                    onChange={(opt) => handleSelectChange('wingId', opt)}
                    disabled={!formData.siteId}
                />

                <CustomSelect
                    label={<>Floor <span className="text-red-500">*</span></>}
                    options={floors}
                    value={selectedFloor}
                    onChange={(opt) => handleSelectChange('floorId', opt)}
                    disabled={!formData.wingId}
                />

                <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1" htmlFor="unitName">
                        Unit / Flat Number <span className="text-red-500">*</span>
                    </label>
                    <input
                        className="w-full px-3 py-2 border rounded-md text-sm outline-none focus:border-indigo-500 transition-colors"
                        type="text"
                        id="unitName"
                        value={formData.unitName}
                        onChange={handleInputChange}
                        placeholder="e.g., A-101"
                    />
                </div>

                <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1" htmlFor="sqft">
                        Area (Sq.ft)
                    </label>
                    <input
                        className="w-full px-3 py-2 border rounded-md text-sm outline-none focus:border-indigo-500 transition-colors"
                        type="number"
                        id="sqft"
                        value={formData.sqft}
                        onChange={handleInputChange}
                        placeholder="e.g., 1200"
                    />
                </div>

                <CustomSelect
                    label={<>Status <span className="text-red-500">*</span></>}
                    options={[{ label: 'Active', value: 'Active' }, { label: 'Inactive', value: 'Inactive' }]}
                    value={formData.isActive ? { label: formData.isActive, value: formData.isActive } : null}
                    onChange={(opt) => handleSelectChange('isActive', opt)}
                />
            </div>

            <div className="flex justify-between mt-auto border-t pt-4">
                <button
                    onClick={() => navigate("/zentres/master-management/unit")}
                    className="px-3 py-2 border rounded-md text-xs flex items-center gap-2 text-gray-600 hover:bg-gray-50 transition-colors"
                >
                    <FontAwesomeIcon icon={faAngleLeft} />
                    Cancel
                </button>

                <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                    style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = primaryColor();
                        e.currentTarget.style.color = '#FFFFFF';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = secondaryColor();
                        e.currentTarget.style.color = primaryColor();
                    }}
                >
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEdit ? 'Update' : 'Submit')}
                    <FontAwesomeIcon icon={faAngleRight} />
                </button>
            </div>
        </div>
    );
};

export default UnitForm;
